"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Check } from "lucide-react";
import { useLocale } from "next-intl";
import { Link } from "@/i18n/routing";
import Image from "next/image";

import React from "react";
import { formatPrice } from "@/lib/helpers/pricing-helper";
import { getCustomerCountry } from "@/lib/helpers/location-helper";
import { PricingFreeBadge } from "@/components/pricing-free-badge";

const PricingCards = ({ t }: { t: any }) => {
  const locale = useLocale();
  const [sliderValue, setSliderValue] = React.useState<number[]>([50]);
  const [formattedPrices, setFormattedPrices] = React.useState<string[]>([]);
  const [userCountry, setUserCountry] = React.useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

  const prices = React.useMemo(() => [
    {
      plan: t("starter.title"),
      price: t("starter.price"),
      clients: t("starter.clients"),
      type: t("starter.type"),
      description: t("starter.description"),
      advantages: [
        t("starter.features.0"),
        t("starter.features.1"),
        t("starter.features.2"),
        t("starter.features.3"),
        t("starter.features.4"),
      ],
    },
    {
      plan: t("pro.title"),
      price: t("pro.price"),

      type: t("pro.type"),
      description: t("pro.description"),
      advantages: [
        t("pro.features.0"),
        t("pro.features.1"),
        t("pro.features.2"),
        t("pro.features.3"),
        t("pro.features.4"),
        t("pro.features.5"),
        t("pro.features.6"),
      ],
    },
    {
      plan: t("elite.title"),
      price: t("elite.price"),
      clients: t("elite.clients"),
      type: t("elite.type"),
      description: t("elite.description"),
      advantages: [
        t("elite.features.0"),
        t("elite.features.1"),
        t("elite.features.2"),
        t("elite.features.3"),
        t("elite.features.4"),
        t("elite.features.5"),
        t("elite.features.6"),
      ],
    },
  ], [t]);

  const badgeMessage = t("badge.message");
  const badgeAlign = locale === "ar" ? "left" : "right";

  const handleSliderChange = (value: number[]) => {
    setSliderValue(value);
    setHasUserInteracted(true);
  };
  
  React.useEffect(() => {
    const fetchCountry = async () => {
      try {
        const res = await getCustomerCountry();
        setUserCountry(res);
  
        localStorage.setItem('country', res);
        localStorage.setItem('country_expiry', String(Date.now() + 1000 * 60 * 60 * 24));
      } catch (err) {
        console.error("Failed to fetch country", err);
      }
    };
  
    const cached = localStorage.getItem('country');
    const expiry = localStorage.getItem('country_expiry');
    const hasExpired = !expiry || Date.now() > Number(expiry);
    
    if (cached && !hasExpired) {
      setUserCountry(cached);
    } else {
      fetchCountry();
    }
  }, []);
  
  

  const hasFormattedPrices = formattedPrices.length > 0;

  React.useEffect(() => {
    if (!hasUserInteracted && hasFormattedPrices) return;
  
    async function updatePrices() {
      const pricesFormatted = await Promise.all(
        prices.map((item, index) =>{
          return formatPrice(locale, index, item, userCountry, sliderValue[0])}
        )
      );
      setFormattedPrices(pricesFormatted);
    }
  
    updatePrices();
  }, [sliderValue, hasUserInteracted, userCountry, locale, prices, hasFormattedPrices]);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {prices.map((item, index) => (
        <Card
          key={index}
          className="rounded-[3rem] text-primary-foreground bg-gradient-to-b from-primary to-second-primary min-h-[1100px] flex flex-col gap-6 relative"
        >
          {locale === "en" ? (
            <Image
              src="/free_trial_icon.png"
              alt="Free trial"
              width={240}
              height={240}
              className="absolute top-4 right-4 w-40 h-40 object-contain drop-shadow-2xl"
            />
          ) : (
            <Image
              src="/free_trial_icon_ar.png"
              alt="نسخة تجريبية مجانية"
              width={240}
              height={240}
              className="absolute top-4 left-4 w-40 h-40 object-contain drop-shadow-2xl"
            />
          )}
          <CardHeader className="gap-5 pb-0">
            <span className="font-poppins font-semibold text-xl">
              {item.plan}
            </span>
            <CardTitle className="text-6xl">
              {formattedPrices[index] ?? ""}
              </CardTitle>
            <span className="text-xs font-poppins">{item.type}</span>
            {item.clients ? (
              <span className="text-5xl">{item.clients}</span>
            ) : (
              <Slider
                className="h-10"
                defaultValue={sliderValue}
                onValueChange={(value) => handleSliderChange(value)}
                client={t("client")}
                locale={locale}
                min={10}
                max={150}
                step={10}
                // setPrice={setPrice}
              />
            )}
            <CardDescription className="font-poppins text-primary-foreground">
              {item.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-0 flex flex-col justify-center items-start gap-6 font-poppins">
            {item.advantages.map((advantage, index) => (
              <div
                key={advantage}
                className="flex justify-center items-center gap-3"
              >
                <div>
                  <Check className="w-10 h-10 p-2 rounded-full bg-gradient-to-l from-primary to-second-primary" />
                </div>
                {advantage}
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex-1">
            <div className="w-full flex flex-col gap-3 self-end">
              <Button
                className="rounded-full w-full text-3xl pb-6 pt-7"
                variant="secondary"
              >
                <Link href="/auth/signup">{t("start")}</Link>
              </Button>
              <span className="text-sm font-semibold text-primary-foreground/90 text-center">
                {t("no_credit_card")}
              </span>
            </div>
            {/* Temporarily hiding "3 Months Free" messaging until promo returns */}
            {/* <PricingFreeBadge
              message={badgeMessage}
              align={badgeAlign}
              className="mt-4"
              variant="subtext"
            /> */}
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default PricingCards;
